import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    MessageSquare,
    Trash2,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    User,
    Bot
} from 'lucide-react';

interface Comment {
    _id: string;
    text: string;
    username: string;
    userId: string;
    characterId?: {
        _id: string;
        name: string;
    };
    createdAt: string;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

const AdminComments: React.FC = () => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, pages: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchComments = async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '50'
            });

            const response = await fetch(`${API_URL}/admin/comments?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setComments(data.comments);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, []);

    const handleDeleteComment = async (commentId: string) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/comments/${commentId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if ((await response.json()).success) {
                fetchComments(pagination.page);
                setShowDeleteModal(false);
                setSelectedComment(null);
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                        Moderación de Comentarios
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {pagination.total} comentarios en total
                    </p>
                </div>
                <button
                    onClick={() => fetchComments(pagination.page)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                        color: 'white',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={18} />
                    Actualizar
                </button>
            </div>

            {/* Comments List */}
            <div style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden'
            }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                        <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                ) : comments.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                        <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <p>No hay comentarios para moderar</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {comments.map((comment, index) => (
                            <div
                                key={comment._id}
                                style={{
                                    padding: '20px',
                                    borderBottom: index < comments.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    display: 'flex',
                                    gap: '16px'
                                }}
                            >
                                {/* Comment Content */}
                                <div style={{ flex: 1 }}>
                                    {/* Header */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            color: '#4ecdc4'
                                        }}>
                                            <User size={16} />
                                            <span style={{ fontWeight: '600' }}>{comment.username}</span>
                                        </div>
                                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            color: '#ff6b6b'
                                        }}>
                                            <Bot size={16} />
                                            <span>{comment.characterId?.name || 'Personaje eliminado'}</span>
                                        </div>
                                        <span style={{
                                            marginLeft: 'auto',
                                            color: 'rgba(255,255,255,0.4)',
                                            fontSize: '13px'
                                        }}>
                                            {formatDate(comment.createdAt)}
                                        </span>
                                    </div>

                                    {/* Comment Text */}
                                    <p style={{
                                        color: 'rgba(255,255,255,0.85)',
                                        fontSize: '15px',
                                        lineHeight: '1.5',
                                        padding: '12px 16px',
                                        background: 'rgba(0,0,0,0.2)',
                                        borderRadius: '12px',
                                        margin: 0
                                    }}>
                                        {comment.text}
                                    </p>
                                </div>

                                {/* Delete Button */}
                                <button
                                    onClick={() => { setSelectedComment(comment); setShowDeleteModal(true); }}
                                    style={{
                                        padding: '10px',
                                        background: 'rgba(255, 107, 107, 0.2)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#ff6b6b',
                                        cursor: 'pointer',
                                        alignSelf: 'flex-start'
                                    }}
                                    title="Eliminar comentario"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <button
                            onClick={() => fetchComments(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            style={{
                                padding: '8px 14px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === 1 ? 0.5 : 1
                            }}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Página {pagination.page} de {pagination.pages}
                        </span>
                        <button
                            onClick={() => fetchComments(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                            style={{
                                padding: '8px 14px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === pagination.pages ? 0.5 : 1
                            }}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedComment && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        width: '450px',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                        textAlign: 'center'
                    }}>
                        <AlertTriangle size={48} style={{ color: '#ff6b6b', marginBottom: '16px' }} />
                        <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '16px' }}>¿Eliminar Comentario?</h2>

                        <div style={{
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '24px',
                            textAlign: 'left'
                        }}>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                                <strong>{selectedComment.username}</strong> en {selectedComment.characterId?.name}:
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                                "{selectedComment.text.substring(0, 100)}{selectedComment.text.length > 100 ? '...' : ''}"
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteComment(selectedComment._id)}
                                disabled={actionLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {actionLoading ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminComments;
